import React, { FC, ReactNode } from 'react'
import { Box, Flex } from '@chakra-ui/react'
import { differenceInDays } from 'date-fns'
import { Trans, Plural } from '@lingui/macro'

import { CertificationEnrolmentStatusEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'

import IconBadge from '../../../components/common/icon/IconBadge'
import IconTick from '../../../components/common/icon/IconTick'
import IconX from '../../../components/common/icon/IconX'
import { TypeOfElement } from '../../../../../types/generic'
import { AssignedCertificationsQuery } from '../../home-page/components/your-certifications/YourCertifications.codegen'
import { brandColors } from 'shared/basicPalette'
import {
  fontSizes,
  fontWeights
} from 'front-end/src/components/chakraThemeExtension'
import { StatusProgressDue } from '../../../components/common/status/Status'
import { formatDateShorter } from '../../../utils/dateHelpers'

export const CertificationName: FC = ({ children }) => {
  return (
    <Box fontWeight={fontWeights[500]} color={brandColors.grey.dark}>
      {children}
    </Box>
  )
}

export const CertificationStatus: FC<{ children: ReactNode }> = ({
  children
}) => {
  return (
    <Box position="absolute" right="14px" top="14px">
      {children}
    </Box>
  )
}

export const AwardedLabel: FC<{ date: string }> = ({ date }) => {
  return (
    <Box
      color={brandColors.grey.light}
      paddingLeft="8px"
      paddingRight="8px"
      fontSize={fontSizes.sm}
      css={{ position: 'absolute', right: '4px', bottom: '8px' }}
    >
      <Trans>Awarded {formatDateShorter(date)}</Trans>
    </Box>
  )
}

export const ProgressRow: FC<{
  daysExpired: number | null
  progress: number | null | undefined
}> = ({ daysExpired, progress }) => {
  const ProgressBar = () => (
    <Box
      borderRadius="md"
      w={['80px', '70px']}
      h="10px"
      mb="4px"
      bg={brandColors.grey.lighter}
    >
      <Box
        roundedLeft={'5px'}
        bg={brandColors.yellow.mid}
        h="10px"
        w={`${progress}%`}
      />
    </Box>
  )

  if (daysExpired) {
    return (
      <Flex alignItems="center">
        <Box mb="4px" color={brandColors.red[500]}>
          <Trans>
            Certification expired{' '}
            <strong>
              <Plural value={daysExpired} one="# day" other="# days" />
            </strong>{' '}
            ago
          </Trans>
        </Box>
      </Flex>
    )
  } else {
    return progress ? (
      progress == 100 ? (
        <Flex alignItems="center">
          <Box
            mb="4px"
            display={['none', 'block']}
            color={brandColors.green.mid}
          >
            <Trans>Awarded</Trans>
          </Box>
        </Flex>
      ) : (
        <Flex alignItems="center">
          <Flex
            mb="4px"
            display={['none', 'block']}
            color={brandColors.grey[500]}
          >
            <Trans>Certification in progress {progress}%</Trans>
          </Flex>
          <Box ml={['0px', '8px']}>
            <ProgressBar />
          </Box>
        </Flex>
      )
    ) : (
      <Box mb="4px" display={['none', 'block']} color={brandColors.grey.light}>
        <Trans>Certification not started</Trans>
      </Box>
    )
  }
}

export const BackgroundBadge = (
  isExpired: boolean | null,
  progress: number | null | undefined
) => {
  if (isExpired)
    return 'linear-gradient(135deg, #EF6969 0%, #D55A5A 100%), linear-gradient(270deg, rgba(8,16,24,0.09) 0%, rgba(255,255,255,0.00) 13%);'
  if (progress === null) {
    return 'linear-gradient(135deg, #F3F5F6 0%, #F3F5F6 100%)'
  }
  if (!progress) {
    return 'linear-gradient(135deg, #DBE2E6 0%, #B1BFC5 100%)'
  } else if (progress == 100) {
    return 'linear-gradient(270deg, rgba(8,16,24,0.09) 0%, rgba(255,255,255,0.00) 14%), linear-gradient(135deg, rgba(0,187,119,0.60) 0%, rgba(0,187,119,0.84) 100%)'
  } else {
    return 'linear-gradient(270deg, rgba(8,16,24,0.04) 0%, rgba(255,255,255,0.00) 14%), linear-gradient(135deg, rgba(255,196,2,0.65) 0%, #FFC402 100%)'
  }
}

export const ColorBadge = (
  isExpired: boolean | null,
  progress: number | null | undefined
) => {
  if (isExpired) return brandColors.red.mid
  if (!progress) {
    return brandColors.grey.light
  } else if (progress == 100) {
    return brandColors.green[600]
  } else {
    return brandColors.yellow.mid
  }
}

export const Badge: FC<{
  isExpired: boolean | null
  progress: number | null | undefined
}> = ({ isExpired, progress }) => {
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      roundedLeft="8px"
      h={'100%'}
      w={['60px', '100px']}
      minWidth={['60px', '100px']}
      backgroundImage={BackgroundBadge(isExpired, progress)}
    >
      <Flex
        position="relative"
        boxShadow="2px 2px 8px 0 rgba(8,15,24,0.03)"
        color={ColorBadge(isExpired, progress)}
        alignItems="center"
        justifyContent="center"
        bg={brandColors.white}
        borderRadius="50%"
        h={['36px', '60%']}
        w={['36px', '60%']}
        fontSize={['20px', '30px']}
      >
        <IconBadge />
        {isExpired ? (
          <Flex position="absolute" top="0" right="0">
            <IconX />
          </Flex>
        ) : (
          <>
            {progress == 100 && (
              <Flex position="absolute" top="0" right="0">
                <IconTick />
              </Flex>
            )}
          </>
        )}
      </Flex>
    </Flex>
  )
}

export const CertificationEnrolmentCard: FC<{
  certificationEnrolment: TypeOfElement<
    AssignedCertificationsQuery['me']['certificationEnrolments']
  >
  small?: boolean
  style?: any
}> = ({ certificationEnrolment, small = false, style }) => {
  const daysExpired =
    certificationEnrolment.status == CertificationEnrolmentStatusEnum.EXPIRED &&
    certificationEnrolment.expires_at
      ? differenceInDays(
          new Date(),
          new Date(certificationEnrolment.expires_at)
        )
      : null

  const isBigAndHasLongName =
    (!small || certificationEnrolment.certification.name.length) > 40
  return (
    <Flex
      p="0px"
      borderRadius="8px"
      w="100%"
      h="100px"
      bg={brandColors.white}
      position="relative"
      {...style}
    >
      {certificationEnrolment.target_date &&
        !certificationEnrolment.completed_at &&
        certificationEnrolment?.status && (
          <CertificationStatus>
            <StatusProgressDue
              dueDate={certificationEnrolment.target_date}
              status={certificationEnrolment?.status}
            />
          </CertificationStatus>
        )}
      {certificationEnrolment.status ==
        CertificationEnrolmentStatusEnum.COMPLETED && (
        // @ts-expect-error 9/11/2020
        <AwardedLabel date={certificationEnrolment.completed_at} />
      )}
      <Badge
        isExpired={!!daysExpired}
        progress={certificationEnrolment.progress}
      />
      <Flex
        fontSize={fontSizes.sm}
        direction="column"
        p="16px"
        justifyContent="space-evenly"
      >
        <ProgressRow
          daysExpired={daysExpired}
          progress={certificationEnrolment.progress}
        />
        <Box
          lineHeight={isBigAndHasLongName ? '22px' : '24px'}
          fontSize={isBigAndHasLongName ? 'lg' : '2xl'}
        >
          <CertificationName>
            {certificationEnrolment.certification.name}
          </CertificationName>
        </Box>
      </Flex>
    </Flex>
  )
}
